import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MeResponse } from "../../shared/api-types";
import { ApiRequestError, api } from "../api/client";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "error"; message: string }
  | { status: "authenticated"; me: MeResponse };

interface AuthApi {
  state: AuthState;
  refresh: () => Promise<void>;
  signedIn: (me: MeResponse) => void;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  // Every state change bumps the generation; a /me response only applies if nothing newer happened
  // meanwhile (e.g. the mount-time 401 must not overwrite a sign-in from the e-mail link).
  const generation = useRef(0);

  const refresh = useCallback(async () => {
    const started = ++generation.current;
    let next: AuthState;
    try {
      next = { status: "authenticated", me: await api<MeResponse>("GET", "/auth/me") };
    } catch (err) {
      next =
        err instanceof ApiRequestError && err.status === 401
          ? { status: "anonymous" }
          : { status: "error", message: err instanceof Error ? err.message : String(err) };
    }
    if (generation.current === started) setState(next);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Cached data belongs to the signed-in person: another account (or signing out) starts from an empty cache, so
  // no menu, import or billing state of the previous session shows up even for a moment.
  const qc = useQueryClient();
  const userId = state.status === "authenticated" ? state.me.user.id : null;
  const lastUser = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (state.status === "loading" || state.status === "error") return;
    if (lastUser.current !== undefined && lastUser.current !== userId) qc.clear();
    lastUser.current = userId;
  }, [state.status, userId, qc]);

  const value = useMemo<AuthApi>(
    () => ({
      state,
      refresh,
      signedIn: (me) => {
        generation.current++;
        setState({ status: "authenticated", me });
      },
      logout: async () => {
        generation.current++;
        try {
          await api("POST", "/auth/logout");
        } finally {
          setState({ status: "anonymous" });
        }
      },
    }),
    [state, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
